/**
 * Which town a business is in, for everything that targets a place: site
 * copy, page targets, rank scans, ads, booking, Sam. The address itself
 * (NAP, schema.org PostalAddress, the footer) stays on Business.city.
 */

/** A coordinate as it arrives: a number, a Prisma Decimal, or a decimal string off the wire. */
type Coordinate = number | string | { toNumber(): number } | null | undefined;

interface PrimaryMarket {
  /** The town, "St. Augustine". */
  name: string;
  /** Two-letter state, "FL", when the record has one. */
  state: string | null;
  /** Where to center on it: the business's own point in its own town, else the town's center; null when unknown. */
  lat: number | null;
  lng: number | null;
}

interface MarketArea {
  name: string;
  state?: string | null;
  isPrimary?: boolean | null;
  latitude?: Coordinate;
  longitude?: Coordinate;
}

interface MarketSource {
  city: string | null;
  state?: string | null;
  latitude?: Coordinate;
  longitude?: Coordinate;
  servesCustomersAtAddress?: boolean;
  serviceAreas?: readonly MarketArea[];
}

function toNumber(value: Coordinate): number {
  if (value == null) {
    return NaN;
  }
  if (typeof value === "object") {
    return value.toNumber();
  }
  return Number(value);
}

/** Both coordinates, or neither: 0,0 is a missing geocode, not a place in the Gulf of Guinea. */
const point = (latitude: Coordinate, longitude: Coordinate): { lat: number | null; lng: number | null } => {
  const lat = toNumber(latitude),
    lng = toNumber(longitude),
    known = Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);
  if (known) {
    return { lat, lng };
  }
  return { lat: null, lng: null };
};

const areaMarket = (area: MarketArea | undefined): PrimaryMarket | null => {
  if (!area) {
    return null;
  }
  return { name: area.name, state: area.state ?? null, ...point(area.latitude, area.longitude) };
};

/**
 * The town the business targets. A storefront (customers come to its address)
 * targets its own city; a service-area business, whose address is hidden,
 * targets the service area the owner made primary. Each falls back to the
 * other, then to the first area. Null means no place at all: callers omit
 * geography rather than interpolate "null" into a headline.
 */
const primaryMarket = (business: MarketSource): PrimaryMarket | null => {
  const ownCity: PrimaryMarket | null = business.city
      ? {
          name: business.city,
          state: business.state ?? null,
          ...point(business.latitude, business.longitude),
        }
      : null,
    primaryArea = areaMarket(business.serviceAreas?.find((serviceArea) => serviceArea.isPrimary)),
    firstArea = areaMarket(business.serviceAreas?.[0]),
    preferred = business.servesCustomersAtAddress === true
      ? (ownCity ?? primaryArea)
      : (primaryArea ?? ownCity),
    market = preferred ?? firstArea;
  // In its own town, the business's point is the precise one; the town's is
  // Only its center. Public payloads strip that point, so they get the town's.
  if (market && ownCity?.lat != null && isPrimaryMarketArea(ownCity, market)) {
    return { ...market, lat: ownCity.lat, lng: ownCity.lng };
  }
  return market;
};

/** "Austin, TX" — never "Austin, null" when the state is missing. */
const formatGeoLabel = (city: string | null, state: string | null): string => [city, state].filter(Boolean).join(", ");

/** "St. Augustine, FL", or "" when there is no market. */
const primaryMarketLabel = (market: PrimaryMarket | null | undefined): string => {
  if (market) {
    return formatGeoLabel(market.name, market.state);
  }
  return "";
};

/** Folds case and accents, so "Cañon City" and "Canon City" are one town. */
function townKey(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[̀-ͯ]/gu, "")
    .trim()
    .toLowerCase();
}

/** Whether an area is the primary market's town. A missing state on either side matches any. */
function isPrimaryMarketArea(
  area: { name: string; state?: string | null },
  market: PrimaryMarket | null | undefined,
): boolean {
  if (!market || townKey(area.name) !== townKey(market.name)) {
    return false;
  }
  return area.state === undefined || area.state === null || area.state === "" || !market.state || area.state.toLowerCase() === market.state.toLowerCase();
}

/** The areas with the primary market's town first; the rest keep their order. */
const primaryMarketFirst = <T extends { name: string; state?: string | null }>(
  areas: readonly T[],
  market: PrimaryMarket | null | undefined,
): T[] => [
  ...areas.filter((area) => isPrimaryMarketArea(area, market)),
  ...areas.filter((area) => !isPrimaryMarketArea(area, market)),
];

export type { PrimaryMarket, MarketSource };
export {
  primaryMarket,
  formatGeoLabel,
  primaryMarketLabel,
  isPrimaryMarketArea,
  primaryMarketFirst,
};
